import { ctw } from '@/common';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/atoms';
import { Check, ExternalLinkIcon, FileText, X } from 'lucide-react';

import { ReportWrapper } from '../../report-shared/components';
import type { RiskIndicator } from '../types';

type CompliancePagesSectionProps = {
  websiteStructureRiskIndicators?: RiskIndicator[] | null;
};

export const CompliancePagesSection = ({
  websiteStructureRiskIndicators,
}: CompliancePagesSectionProps) => {
  return (
    <ReportWrapper
      title="Compliance Pages"
      Icon={FileText}
      tooltip="Verification of required legal and compliance pages on the merchant's website."
      showTooltip={false}
    >
      <div className="space-y-4">
        {(websiteStructureRiskIndicators ?? []).map(indicator => (
          <CompliancePagesItem key={indicator.id} indicator={indicator} />
        ))}
      </div>
    </ReportWrapper>
  );
};

const CompliancePagesItem = ({ indicator }: { indicator: RiskIndicator }) => {
  const { pageContext, name, status, reason, sourceUrl } = indicator;
  const displayName = pageContext || name;

  if (!displayName) {
    return null;
  }

  return (
    <div className="flex items-center gap-3 py-1">
      <div className="flex-shrink-0">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {!status || status === 'missing' ? (
                <div className="w-5 h-5 rounded-full bg-gray-500/10 flex items-center justify-center">
                  <X className="h-2.5 w-2.5 text-gray-500" strokeWidth={6} />
                </div>
              ) : status === 'detected' ? (
                <div className="w-5 h-5 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Check className="h-2.5 w-2.5 text-green-500" strokeWidth={6} />
                </div>
              ) : (
                <div className="w-5 h-5 flex items-center justify-center text-neutral-400 font-semibold text-sm cursor-default bg-neutral-200 rounded-full">
                  ?
                </div>
              )}
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="text-sm">{reason}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="flex items-center gap-2 flex-1">
        <span className={ctw('font-medium text-black', status === 'missing' && 'opacity-20')}>
          {displayName}
        </span>
        {sourceUrl && (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800"
            title={`Visit ${displayName} page`}
          >
            <ExternalLinkIcon className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
};
